import { PlaneView } from '../../../../view/views/PlaneView';

/**
 * Base class for cursor implementations that target viewport
 * canvas parent elements. Provides shared view storage and
 * DOM-targeting helpers used by both CSS-cursor-based cursors
 * ({@link SvgCursor}) and overlay-based cursors
 * ({@link WindowingCursor}).
 */
export abstract class ViewportCursor {
    protected readonly _views: PlaneView[] | null;

    constructor(views: PlaneView[] | null) {
        this._views = views;
    }

    /**
     * Returns the parent elements of all viewport canvases,
     * or null if no views are available.
     */
    protected getCanvasParents(): (HTMLElement | null | undefined)[] | null {
        const canvases = this._views?.map((view) => view.get2DCanvas);
        if (!canvases) {
            return null;
        }
        return canvases.map((canvas) => canvas?.parentElement);
    }

    /**
     * Set the CSS cursor style on all canvas parent elements.
     * When no parents are available, falls back to
     * document.body.
     */
    protected setParentsCursorStyle(style: string): void {
        const parents = this.getCanvasParents();
        if (parents) {
            parents.forEach((parent) => {
                if (parent) {
                    parent.style.cursor = style;
                    // Also cover SVG overlays that are siblings of the canvas parent
                    const viewportContainer = parent.closest(
                        '[data-viewport-container]'
                    ) as HTMLElement | null;
                    if (viewportContainer) {
                        viewportContainer.style.cursor = style;
                    }
                }
            });
        } else {
            document.body.style.cursor = style;
        }
    }

    /**
     * Reset the CSS cursor on all canvas parent elements back
     * to 'default'. Falls back to document.body when no
     * parents are available.
     */
    protected resetParentsCursor(): void {
        this.setParentsCursorStyle('default');
    }
}
